class StockPreorderBadge extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.sectionId = this.dataset.sectionId;
    this.labelEl = this.querySelector('[data-stock-preorder-label]');
    this.descEl = this.querySelector('[data-stock-preorder-description]');

    this.parseVariantData();

    this.onQuantityChange = this.update.bind(this);
    this.quantityUnsub = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityChange);
    this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId && event.data.sectionId !== this.sectionId) return;
      this.syncInventoryFromVariant(event?.data?.variant);
      requestAnimationFrame(() => this.update());
    });
    this.cartUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      window.setTimeout(() => this.update(), 200);
    });

    const productInfo = this.getProductInfo();
    const quantityInput = productInfo?.querySelector('.quantity__input');
    quantityInput?.addEventListener('input', this.onQuantityChange);
    quantityInput?.addEventListener('change', this.onQuantityChange);

    this.update();
  }

  disconnectedCallback() {
    this.quantityUnsub?.();
    this.variantUnsub?.();
    this.cartUnsub?.();

    const productInfo = this.getProductInfo();
    const quantityInput = productInfo?.querySelector('.quantity__input');
    quantityInput?.removeEventListener('input', this.onQuantityChange);
    quantityInput?.removeEventListener('change', this.onQuantityChange);
  }

  parseVariantData() {
    const dataEl = this.querySelector('[data-stock-preorder-variants]');
    if (!dataEl) return;

    try {
      this.variantData = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.variantData = {};
    }
  }

  getProductInfo() {
    return (
      this.closest('product-info') ||
      document.querySelector(`product-info[data-section="${this.sectionId}"]`)
    );
  }

  getVariantId() {
    return this.getProductInfo()?.querySelector('input[name="id"]')?.value;
  }

  getQuantityInput() {
    return this.getProductInfo()?.querySelector('.product-form__quantity .quantity__input');
  }

  getSelectedQuantity() {
    const input = this.getQuantityInput();
    const qty = parseInt(input?.value, 10);
    return isNaN(qty) || qty < 1 ? 1 : qty;
  }

  getCartQuantity() {
    const input = this.getQuantityInput();
    return parseInt(input?.dataset.cartQuantity, 10) || 0;
  }

  syncInventoryFromVariant(variant) {
    if (!variant?.id) return;

    const mapKey = `product_inventory_array_${this.productId}`;
    if (!window[mapKey]) window[mapKey] = {};
    if (typeof variant.inventory_quantity !== 'undefined') {
      window[mapKey][variant.id] = variant.inventory_quantity;
    }

    if (this.variantData?.[variant.id]) {
      this.variantData[variant.id].qty =
        typeof variant.inventory_quantity !== 'undefined'
          ? Number(variant.inventory_quantity)
          : this.variantData[variant.id].qty;
    }
  }

  getVariantInventory(variantId) {
    if (this.variantData?.[variantId]) {
      return this.variantData[variantId];
    }

    const mapKey = `product_inventory_array_${this.productId}`;
    const qty = window[mapKey]?.[variantId];
    if (typeof qty !== 'undefined') {
      return { qty: parseInt(qty, 10), managed: true };
    }

    const input = this.getQuantityInput();
    if (input && input.dataset.inventoryQuantity !== undefined) {
      const parsed = parseInt(input.dataset.inventoryQuantity, 10);
      return {
        qty: isNaN(parsed) ? 0 : parsed,
        managed: true,
      };
    }

    return { qty: NaN, managed: false };
  }

  resolveStatus(inventory, selectedQty, cartQty) {
    if (!inventory.managed || isNaN(inventory.qty)) {
      return 'stock';
    }

    if (inventory.qty <= 0) {
      return 'preorder';
    }

    const availableFromStock = Math.max(0, inventory.qty - cartQty);
    return selectedQty > availableFromStock ? 'preorder' : 'stock';
  }

  update() {
    const variantId = this.getVariantId();
    if (!variantId) return;

    const inventory = this.getVariantInventory(variantId);
    const selectedQty = this.getSelectedQuantity();
    const cartQty = this.getCartQuantity();
    const status = this.resolveStatus(inventory, selectedQty, cartQty);

    this.renderStatus(status);
  }

  renderStatus(status) {
    const isStock = status === 'stock';
    const label = isStock ? this.dataset.labelStock : this.dataset.labelPreorder;
    const description = isStock
      ? this.dataset.descriptionStock
      : this.dataset.descriptionPreorder;

    this.classList.remove(
      'product-purchase-banner--stock',
      'product-purchase-banner--preorder'
    );
    this.classList.add(isStock ? 'product-purchase-banner--stock' : 'product-purchase-banner--preorder');

    if (this.labelEl) this.labelEl.textContent = label;
    if (this.descEl) this.descEl.textContent = description;

    this.setAttribute('data-status', status);
  }
}

if (!customElements.get('stock-preorder-badge')) {
  customElements.define('stock-preorder-badge', StockPreorderBadge);
}
